import { useState, useEffect } from "react";
import { createOrder, getOrderPrice } from "@/shared/api/orders/api-orders";
import type { OrderPrice } from "@/shared/api/types";
import { useTableStore } from "@/shared/stores/tableStore";
import { useServiceWorkflowStore } from "@/shared/stores/serviceWorkflowStore";
import { useTariffByServiceType } from "@/shared/hooks/useTariffByServiceType";
import { useToastStore } from "@/shared/stores/toastStore";
import { useAuthStore } from "@/shared/stores/authStore";
import { pollTableStatus } from "@/shared/lib/polling";
import { useTableStatusStore } from "@/shared/stores/tableStatusStore";
import { classifyApiError } from "@/shared/lib/apiErrorHandler";

export const usePayment = () => {
  const { serviceType, stage, navigateToStage, replaceStage } =
    useServiceWorkflowStore();
  const { showToast } = useToastStore();
  const { tariff } = useTariffByServiceType(serviceType);
  const { tableId } = useTableStore();
  const { profile } = useAuthStore();
  const { fetchTableStatus } = useTableStatusStore();
  const [orderPrice, setOrderPrice] = useState<OrderPrice | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [promocode, setPromocode] = useState("");
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoSuccess, setPromoSuccess] = useState<string | null>(null);
  const [isPromoValid, setIsPromoValid] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (stage === "payment" && tariff && serviceType) {
      const load = async () => {
        try {
          const priceData = await getOrderPrice({
            tariff_id: tariff.id,
            service_type: serviceType,
            service_id: tableId || undefined,
          });
          setOrderPrice(priceData);
        } catch {
          showToast("Ошибка получения информации о цене", "error", 4000);
        }
      };
      load();
    }
  }, [stage, tariff, tableId, serviceType, showToast]);

  const handleApplyPromocode = async () => {
    if (!tariff || !promocode || !serviceType) return;
    setPromoError(null);
    setPromoSuccess(null);

    try {
      const priceData = await getOrderPrice({
        tariff_id: tariff.id,
        promocode_code: promocode,
        service_type: serviceType,
        service_id: tableId || undefined,
      });

      setOrderPrice(priceData);

      if (priceData.promocode && priceData.promocode.valid) {
        setIsPromoValid(true);
        setPromoSuccess("Промокод применен!");
      } else {
        setIsPromoValid(false);
        setPromoError("Промокод недействителен");
      }
    } catch {
      setIsPromoValid(false);
      setPromoError("Ошибка применения промокода");
    }
  };

  const handleProceedToPayment = async (paymentId: string | null) => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setEmailError("Введите корректный E-mail для чека");
      return;
    }
    if (!tableId) {
      showToast(
        "ID таблицы не найден. Пожалуйста, вернитесь и введите ссылку на таблицу.",
        "error",
      );
      return;
    }
    if (!paymentId) {
      navigateToStage("payment_widget");
    } else {
      if (!tariff || !serviceType) {
        showToast("Ошибка проведения оплаты", "error");
        replaceStage("status");
        return;
      }

      setIsProcessing(true);

      try {
        await createOrder({
          customer_email: email,
          tariff_id: tariff.id,
          services: [
            {
              service_type: serviceType,
              service_id: tableId,
            },
          ],
          save_payment_method: true,
          start_subscription: true,
          promocode_code: isPromoValid ? promocode : null,
          saved_payment_method_id: paymentId,
        });

        try {
          await pollTableStatus(tableId, {
            isReady: (subscriptionType) => subscriptionType === "paid",
          });
          await fetchTableStatus(tableId, tariff, profile);
          showToast("Оплата успешно произведена", "success");
          replaceStage("payment");
        } catch {
          showToast(
            "Оплата прошла, но статус пока не обновился. Пожалуйста, проверьте через минуту.",
            "info",
            5000,
          );
          await fetchTableStatus(tableId, tariff, profile).catch(() => {});
          replaceStage("status");
        }
      } catch (createOrderError) {
        const classifiedError = classifyApiError(createOrderError);
        showToast(classifiedError.message, "error", 5000);
        replaceStage("payment");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return {
    orderPrice,
    email,
    setEmail,
    emailError,
    setEmailError,
    promocode,
    setPromocode,
    promoError,
    promoSuccess,
    isPromoValid,
    isProcessing,
    handleApplyPromocode,
    handleProceedToPayment,
  };
};
